/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconBookmark from '@apollo/icons/default/IconBookmark.svg';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import IconDoubleChevronDown from '@apollo/icons/default/IconDoubleChevronDown.svg';
import IconDoubleChevronUp from '@apollo/icons/default/IconDoubleChevronUp.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import { IconButton, customColors } from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { clamp } from 'lodash';
import React from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { STABLE_DEFAULT_EMBEDDABLE_EXPLORER_CONFIG } from 'src/app/embeddableExplorer/initialConfigValues';
import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { ResizerStyles } from 'src/components/common/resizerStyles/ResizerStyles';
import { SplitPane } from 'src/components/common/splitPane/SplitPane';
import { useLocalStorage } from 'src/hooks/useLocalStorage';

import { hotkeys } from '../../../../hooks/useKeypressListener/useKeypressListener';
import { useGraphRef } from '../../hooks/useGraphRef';
import { TabSwitcher } from '../components/tabSwitcher/TabSwitcher';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { EditorBottomTab } from '../helpers/EditorBottomTab';
import { ExplorerDocsTab } from '../helpers/ExplorerDocsTab';
import { useAnalyticsContext } from '../hooks/useAnalyticsContext';
import { SearchStateAction } from '../hooks/useExplorerState/useSearchState';
import {
  GraphIdentifier,
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../hooks/usePerGraphIdentifierLocalStorage';

import { DocsToggle } from './docsToggle/DocsToggle';

const MIN_VARIABLES_PANEL_HEIGHT = 56; // min panel height (48) + bottom padding (8)
const RELAXED_VARIABLE_PANEL_HEIGHT = 188;
const LEFT_PANEL_CLOSED_WIDTH = 0;
const SPLIT_PANE_DIVIDER_WIDTH = 16;
const LEFT_PANEL_MIN_WIDTH = 240;
const OPERATION_PANEL_MIN_WIDTH = 200;
const RESPONSE_PANEL_MIN_WIDTH = 200;
const OPERATION_AND_RESPONSE_MIN_WIDTH =
  OPERATION_PANEL_MIN_WIDTH +
  SPLIT_PANE_DIVIDER_WIDTH * 2 +
  RESPONSE_PANEL_MIN_WIDTH;

const useDocsTab = ({
  localStorageGraphIdentifier,
  explorerBehavioralFlags,
  setLeftPaneOpen,
}: {
  localStorageGraphIdentifier: GraphIdentifier;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  setLeftPaneOpen: (value: React.SetStateAction<boolean>) => void;
}) => {
  const [docsTabMap, setDocsTabMap] = useLocalStorage('docsTab');
  const graphRef = useGraphRef();
  // For the embeddable Explorer, we support different docs tab for
  // embeds from a graphRef and embeds with manual schema entry.
  // We don't folks to have come from a graphRef embed and see the
  // operation collections tab in their manual schema embed, so we
  // key whether or not there is a graph.
  const docsTabLocalStorageKey = isEmbeddableExplorerRoute()
    ? `${localStorageGraphIdentifier.domain}${
        graphRef ? '-withGraphRef' : '-withManualSchema'
      }`
    : localStorageGraphIdentifier.domain;

  const docsTab: ExplorerDocsTab =
    explorerBehavioralFlags.showOnlyDocumentationTab
      ? 'Documentation'
      : (docsTabMap[docsTabLocalStorageKey] ?? 'Documentation');
  const setDocsTab = React.useCallback(
    (newDocsTab: ExplorerDocsTab) => {
      setDocsTabMap((currentDocsTabMap) => {
        return {
          ...currentDocsTabMap,
          [docsTabLocalStorageKey]: newDocsTab,
        };
      });
      setLeftPaneOpen(true);
    },
    [docsTabLocalStorageKey, setDocsTabMap, setLeftPaneOpen],
  );
  return { docsTab, setDocsTab };
};

type ExplorerLayoutInfo = ReturnType<typeof useSharedExplorerLayoutInfo>;

// Encapsulate all the layout info that might be affected by explorer operations
// and should be exposed to ExplorerPage (eg command click, refactors, etc)
export function useSharedExplorerLayoutInfo(
  explorerBehavioralFlags: ExplorerBehavioralFlags,
) {
  const [containerWidth, setContainerWidth] = React.useState(
    () => window.innerWidth,
  );

  // Open / closing panels
  const [leftPanelWidthPercent, setLeftPanelWidthPercent] = useLocalStorage(
    'studioReferenceWidth',
  );
  const [leftPaneOpen, setLeftPaneOpen] = useLocalStorage(
    'studioReferenceOpen',
  );

  const [variablesHeight, setVariablesHeight] = useLocalStorage(
    'studioVariablesHeight',
  );

  // Changing panel contents
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: false,
    shouldOnlyStoreForRegisteredGraphs: false,
  });

  // Open docs if the embed param is open
  const [{ docsPanelState }] = usePerGraphIdentifierLocalStorage({
    graphIdentifier: localStorageGraphIdentifier,
    key: 'embeddableExplorerConfigOptions',
    stableInitialValue: STABLE_DEFAULT_EMBEDDABLE_EXPLORER_CONFIG,
  });
  React.useEffect(() => {
    if (docsPanelState) {
      setLeftPaneOpen(docsPanelState === 'open');
    }
  }, [docsPanelState, setLeftPaneOpen]);

  const { docsTab, setDocsTab } = useDocsTab({
    localStorageGraphIdentifier,
    explorerBehavioralFlags,
    setLeftPaneOpen,
  });

  const [activeEditorBottomTab, baseSetActiveEditorBottomTab] =
    React.useState<EditorBottomTab>('Variables');
  const setActiveEditorBottomTab = React.useCallback(
    (tab: EditorBottomTab) => {
      baseSetActiveEditorBottomTab(tab);
      if (variablesHeight === MIN_VARIABLES_PANEL_HEIGHT)
        setVariablesHeight(RELAXED_VARIABLE_PANEL_HEIGHT);
    },
    [variablesHeight, setVariablesHeight],
  );

  return React.useMemo(
    () => ({
      containerWidth,
      setContainerWidth,
      leftPanelWidthPercent,
      setLeftPanelWidth: (width: number) => {
        if (width === LEFT_PANEL_CLOSED_WIDTH) {
          setLeftPaneOpen(false);
        } else {
          setLeftPaneOpen(true);
          setLeftPanelWidthPercent(width / containerWidth);
        }
      },
      leftPaneOpen,
      setLeftPaneOpen,
      variablesHeight,
      setVariablesHeight,
      docsTab,
      setDocsTab,
      activeEditorBottomTab,
      setActiveEditorBottomTab,
    }),
    [
      containerWidth,
      setContainerWidth,
      leftPanelWidthPercent,
      setLeftPanelWidthPercent,
      leftPaneOpen,
      setLeftPaneOpen,
      variablesHeight,
      setVariablesHeight,
      docsTab,
      setDocsTab,
      activeEditorBottomTab,
      setActiveEditorBottomTab,
    ],
  );
}

const LeftPanelTabs = ({
  explorerBehavioralFlags,
  layoutInfo,
  dispatchSearchAction,
}: {
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  layoutInfo: ExplorerLayoutInfo;
  dispatchSearchAction: (action: SearchStateAction) => void;
}) => {
  const { track } = useAnalyticsContext();
  const { showRunHistory, showExplorerSettings, showOperationCollections } =
    explorerBehavioralFlags;
  const { docsTab, setDocsTab, leftPaneOpen, setLeftPaneOpen } = layoutInfo;

  const layoutTabs = (
    [
      {
        key: 'Documentation',
        tooltipLabel: 'Documentation',
        icon: <IconDocument />,
      },
      {
        key: 'Operation Collections',
        tooltipLabel: 'Operation Collections',
        icon: <IconBookmark />,
        dataTestId: 'tab_switcher_operation_collections',
      },
      {
        key: 'Run History',
        tooltipLabel: 'Run History',
        icon: <IconHistory />,
      },
      {
        key: 'Search',
        tooltipLabel: `Search Schema ( ${hotkeys.toggleSearch.label} )`,
        icon: <IconSearch />,
      },
      {
        key: 'Explorer Settings',
        tooltipLabel: 'Settings',
        icon: <IconSettings />,
      },
    ] as const
  ).filter(
    (tab) =>
      !(
        (tab.key === 'Run History' && !showRunHistory) ||
        (tab.key === 'Explorer Settings' && !showExplorerSettings) ||
        (tab.key === 'Operation Collections' && !showOperationCollections)
      ),
  );

  return (
    <div className="flex flex-none items-center justify-between gap-2 border-b border-primary p-2">
      <TabSwitcher<ExplorerDocsTab | 'Search'>
        tabs={layoutTabs}
        activeTab={docsTab}
        onSelectTab={(selectedTab) => {
          track(`Tab to`, { label: selectedTab });
          if (selectedTab === 'Search') {
            dispatchSearchAction({
              type: 'open',
            });
          } else {
            setDocsTab(selectedTab);
          }
        }}
      />
      <DocsToggle
        on={leftPaneOpen}
        onClick={() => setLeftPaneOpen(!leftPaneOpen)}
      />
    </div>
  );
};

export const ExplorerPaneLayout = ({
  explorerBehavioralFlags,
  layoutInfo,
  dispatchSearchAction,
  operationsPane,
  variablesPane,
  headersPane,
  preflightOperationScriptPane,
  postflightOperationScriptPane,
  environmentVariablesPane,
  explorerDocs,
  operationCollections,
  explorerHistory,
  explorerSettings,
  resultsPane,
  explorerTabs,
  shouldShowPostflightScripts,
}: {
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  layoutInfo: ExplorerLayoutInfo;
  dispatchSearchAction: (action: SearchStateAction) => void;
  // Panel contents
  operationsPane: React.ReactNode;
  variablesPane: React.ReactNode;
  headersPane: React.ReactNode;
  preflightOperationScriptPane: React.ReactNode;
  postflightOperationScriptPane: React.ReactNode;
  environmentVariablesPane: React.ReactNode;
  explorerDocs: React.ReactNode;
  operationCollections: React.ReactNode;
  explorerHistory: React.ReactNode;
  explorerSettings: React.ReactNode;
  resultsPane: React.ReactNode;
  explorerTabs: React.ReactNode;
  shouldShowPostflightScripts: boolean;
}) => {
  const {
    leftPaneOpen,
    leftPanelWidthPercent,
    setLeftPanelWidth,
    setLeftPaneOpen,
    variablesHeight,
    setVariablesHeight,
    docsTab,
    activeEditorBottomTab,
    setActiveEditorBottomTab,
    containerWidth,
    setContainerWidth,
  } = layoutInfo;

  // Resizing panels
  const [rightPanelWidthPercent, setRightPanelWidthPercent] = useLocalStorage(
    'studioResponseWidth',
  );

  const leftPanelOpenWidth = clamp(
    containerWidth * leftPanelWidthPercent,
    LEFT_PANEL_MIN_WIDTH,
    containerWidth - OPERATION_AND_RESPONSE_MIN_WIDTH,
  );
  const operationAndResponseWidth = containerWidth - leftPanelOpenWidth;

  const leftPanelWidth = leftPaneOpen
    ? leftPanelOpenWidth
    : LEFT_PANEL_CLOSED_WIDTH;

  const { track } = useAnalyticsContext();

  const isEditorBottomPanelOpen =
    variablesHeight !== MIN_VARIABLES_PANEL_HEIGHT;

  return (
    <div
      className={classnames(
        'relative isolate flex h-full flex-col bg-secondary-inverted text-primary',
        explorerBehavioralFlags.roundedCorners && 'rounded-lg',
      )}
    >
      <ResizerStyles />
      <style>
        {`
          /* total width */
          .xxx-mac-style-scrollbars::-webkit-scrollbar {
              background-color: transparent;
              width:8px;
          }
          /* background of the scrollbar except button or resizer */
          .xxx-mac-style-scrollbars::-webkit-scrollbar-track {
              background-color:transparent;
          }
          /* scrollbar itself */
          .xxx-mac-style-scrollbars::-webkit-scrollbar-thumb {
              background-color: ${colors.midnight.dark};
              border-radius:16px;
              border: 4px solid transparent;
          }
          /* set button(top and bottom of the scrollbar) */
          .xxx-mac-style-scrollbars::-webkit-scrollbar-button { display:none; }
        `}
      </style>
      <ReactResizeDetector
        handleWidth
        onResize={(w) => {
          if (w) setContainerWidth(w);
        }}
      >
        <SplitPane
          allowResize
          primary="first"
          minSize={LEFT_PANEL_CLOSED_WIDTH}
          maxSize={-OPERATION_AND_RESPONSE_MIN_WIDTH}
          size={leftPanelWidth}
          onChange={setLeftPanelWidth}
          minExpandedSize={LEFT_PANEL_MIN_WIDTH}
          css={css`
            & > .Resizer.vertical {
              margin: 48px 0 0 0;
              border-top: 1px solid ${customColors.border.primary};
              z-index: 10;
              height: auto;
              &:before {
                opacity: 0;
              }
              &:hover:before {
                opacity: ${leftPaneOpen ? 1 : 0};
              }
            }
            & > .Pane2 {
              overflow: hidden;
            }
          `}
        >
          <div className="h-full text-primary">
            <div className="flex h-full flex-col" hidden={!leftPaneOpen}>
              {leftPaneOpen && explorerBehavioralFlags.showLeftPaneToggle && (
                <LeftPanelTabs
                  explorerBehavioralFlags={explorerBehavioralFlags}
                  layoutInfo={layoutInfo}
                  dispatchSearchAction={dispatchSearchAction}
                />
              )}

              <div
                className="flex-1 overflow-auto pb-4 pl-4"
                // visually hiding instead of conditionally rendering to avoid
                // unmount/remount and loss of state and scroll position
                hidden={docsTab !== 'Documentation'}
              >
                {explorerDocs}
              </div>
              <div
                className="flex-1 overflow-auto pb-4 pl-4"
                // visually hiding instead of conditionally rendering to avoid
                // unmount/remount and loss of state and scroll position
                hidden={docsTab !== 'Operation Collections'}
              >
                {operationCollections}
              </div>
              <div
                className="flex flex-1 flex-col overflow-auto pb-4 pl-4"
                // visually hiding instead of conditionally rendering to avoid
                // unmount/remount and loss of state and scroll position
                hidden={docsTab !== 'Run History'}
              >
                {explorerHistory}
              </div>
              <div
                className="flex-1 overflow-auto pb-4 pl-4"
                // visually hiding instead of conditionally rendering to avoid
                // unmount/remount and loss of state and scroll position
                hidden={docsTab !== 'Explorer Settings'}
              >
                {explorerSettings}
              </div>
            </div>
          </div>

          <div className="flex size-full flex-col overflow-x-hidden">
            <div
              className={classnames(
                'flex gap-2 border-b border-primary pr-2',
                !leftPaneOpen && 'pl-2',
              )}
            >
              {!leftPaneOpen && (
                <DocsToggle
                  className="mt-2"
                  on={leftPaneOpen}
                  onClick={() => setLeftPaneOpen(!leftPaneOpen)}
                />
              )}
              {explorerTabs}
            </div>
            <div className="relative flex-1">
              <SplitPane
                className={classnames('mr-2 py-2', !leftPaneOpen && 'ml-2')}
                primary="second"
                allowResize
                minSize={RESPONSE_PANEL_MIN_WIDTH}
                maxSize={
                  -(OPERATION_PANEL_MIN_WIDTH + SPLIT_PANE_DIVIDER_WIDTH)
                }
                size={clamp(
                  containerWidth * rightPanelWidthPercent,
                  RESPONSE_PANEL_MIN_WIDTH,
                  operationAndResponseWidth -
                    OPERATION_PANEL_MIN_WIDTH -
                    SPLIT_PANE_DIVIDER_WIDTH * 2,
                )}
                onChange={(size) =>
                  setRightPanelWidthPercent(size / containerWidth)
                }
                css={css`
                  & > .Resizer.vertical {
                    opacity: 0;
                    &:hover {
                      opacity: 1;
                    }
                  }
                `}
              >
                <div className="flex size-full flex-1 flex-col overflow-hidden">
                  <SplitPane
                    split="horizontal"
                    primary="second" // make the bottom pane the primary pane
                    minSize={MIN_VARIABLES_PANEL_HEIGHT}
                    maxSize={-150} // top pane is never smaller than 100px
                    size={variablesHeight}
                    onChange={setVariablesHeight}
                    css={css`
                      & > .Resizer.horizontal {
                        margin: -6px 0;
                        z-index: 10;
                        &:before {
                          height: 0px;
                          width: 100%;
                          border-radius: 0px;
                          border-top: 1px solid ${customColors.border.primary};
                          transition: border-color 200ms ease-out;
                        }
                      }
                      & > .Resizer.horizontal:hover {
                        &:before {
                          border-color: ${customColors.border.hover};
                        }
                      }
                    `}
                  >
                    {operationsPane}
                    <div className="flex flex-1 flex-col overflow-y-auto rounded-b-lg bg-primary-inverted text-secondary">
                      <div className="flex flex-none items-center justify-between gap-1 overflow-x-auto p-2">
                        <div className="flex items-center gap-1">
                          <TabSwitcher<EditorBottomTab>
                            tabs={[
                              'Variables',
                              ...(explorerBehavioralFlags.showHeadersAndEnvironmentVariables
                                ? [
                                    'Headers' as const,
                                    ...(shouldShowPostflightScripts
                                      ? ['Pre-Operation Script' as const]
                                      : ['Script' as const]),
                                    ...(shouldShowPostflightScripts
                                      ? ['Post-Operation Script' as const]
                                      : []),
                                    ...(!explorerBehavioralFlags.environmentVariablesInSettings
                                      ? ['Environment Variables' as const]
                                      : []),
                                  ]
                                : []),
                            ]}
                            activeTab={
                              isEditorBottomPanelOpen
                                ? activeEditorBottomTab
                                : null
                            }
                            onSelectTab={(selectedTab) => {
                              track(`Tab to`, { label: selectedTab });
                              setActiveEditorBottomTab(selectedTab);
                            }}
                          />
                        </div>
                        <IconButton
                          data-testid="toggle-editor-bottom-panel"
                          aria-label="toggle editor bottom panel"
                          size="sm"
                          variant="hidden"
                          icon={
                            isEditorBottomPanelOpen ? (
                              <IconDoubleChevronDown />
                            ) : (
                              <IconDoubleChevronUp />
                            )
                          }
                          onClick={() => {
                            track(
                              isEditorBottomPanelOpen
                                ? 'Collapse Variables'
                                : 'Expand Variables',
                            );
                            setVariablesHeight(
                              isEditorBottomPanelOpen
                                ? MIN_VARIABLES_PANEL_HEIGHT
                                : RELAXED_VARIABLE_PANEL_HEIGHT,
                            );
                          }}
                        />
                      </div>
                      <div
                        className={classnames('flex-1 overflow-hidden', {
                          hidden:
                            activeEditorBottomTab !== 'Variables' ||
                            !isEditorBottomPanelOpen,
                        })}
                      >
                        {variablesPane}
                      </div>
                      {explorerBehavioralFlags.showHeadersAndEnvironmentVariables && (
                        <>
                          <div
                            className={classnames(
                              'flex flex-1 flex-col overflow-auto px-6 pb-4',
                              {
                                hidden:
                                  activeEditorBottomTab !== 'Headers' ||
                                  !isEditorBottomPanelOpen,
                              },
                            )}
                          >
                            {headersPane}
                          </div>
                          {!explorerBehavioralFlags.environmentVariablesInSettings && (
                            <div
                              className={classnames('flex-1', {
                                hidden:
                                  activeEditorBottomTab !==
                                    'Environment Variables' ||
                                  !isEditorBottomPanelOpen,
                              })}
                            >
                              {environmentVariablesPane}
                            </div>
                          )}
                          {shouldShowPostflightScripts ? (
                            <div>
                              {' '}
                              <div
                                className={classnames(
                                  'flex flex-1 flex-col overflow-auto px-6 pb-4',
                                  {
                                    hidden:
                                      activeEditorBottomTab !==
                                        'Pre-Operation Script' ||
                                      !isEditorBottomPanelOpen,
                                  },
                                )}
                              >
                                {preflightOperationScriptPane}
                              </div>
                              <div
                                className={classnames(
                                  'flex flex-1 flex-col overflow-auto px-6 pb-4',
                                  {
                                    hidden:
                                      activeEditorBottomTab !==
                                        'Post-Operation Script' ||
                                      !isEditorBottomPanelOpen,
                                  },
                                )}
                              >
                                {postflightOperationScriptPane}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={classnames(
                                'flex flex-1 flex-col overflow-auto px-6 pb-4',
                                {
                                  hidden:
                                    activeEditorBottomTab !== 'Script' ||
                                    !isEditorBottomPanelOpen,
                                },
                              )}
                            >
                              {preflightOperationScriptPane}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </SplitPane>
                </div>
                {resultsPane}
              </SplitPane>
            </div>
          </div>
        </SplitPane>
      </ReactResizeDetector>
    </div>
  );
};
